import { collection, getDocs, doc, setDoc, Timestamp, query, where, documentId } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger.ts';
import { getCachedEmployees } from './employeeFirebaseService.ts';
import { findCollapsedDays, type CollapsedDay } from '../../utils/dailyReportShrinkGuard.ts';

// ========================================
// Logger 설정 - MonthlyDataFirebaseService
// ========================================
const logger = createLogger('MonthlyDataFirebaseService');
const DEBUG = false;

// 🔧 FIX C4: yearMonth 기반 Firestore 서버 측 날짜 범위 쿼리
const getMonthlyReportsQuery = (yearMonth: string) => {
  const startDate = `${yearMonth}-01`;
  const [year, month] = yearMonth.split('-').map(Number);
  const nextMonth = month === 12 ? `${year + 1}-01` : `${year}-${String(month + 1).padStart(2, '0')}`;
  const endDate = `${nextMonth}-01`;

  return query(
    collection(db, 'dailyReports'),
    where(documentId(), '>=', startDate),
    where(documentId(), '<', endDate)
  );
};

// 특정 월의 일자별 총수납 데이터 가져오기
export const getMonthlyReceiptData = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 일자별 총수납 데이터 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    DEBUG && logger.info(' dailyReports 월별 쿼리 생성');

    const snapshot = await getDocs(monthlyQuery);
    DEBUG && logger.info(`[Firebase] 전체 문서 개수: ${snapshot.docs.length}`);

    const monthlyReceipts: any[] = [];
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        DEBUG && logger.info(`[Firebase] ${yearMonth} 월 문서 발견: ${docDate}`);

        const data = doc.data();

        let dailyTotal = 0;

        if (data && data.receipts && data.receipts.items && Array.isArray(data.receipts.items)) {
          dailyTotal = data.receipts.items.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
          DEBUG && logger.info(`[Firebase] ${docDate}의 총수납 (실제수납액): ${dailyTotal}`);
        }
        else if (data && data.reportStatus && Array.isArray(data.reportStatus) && data.reportStatus.length > 0) {
          const status = data.reportStatus[0];
          const cashReceipt = parseFloat(status['현금수납'] || status['현금'] || 0);
          const cardReceipt = parseFloat(status['카드수납'] || status['카드'] || 0);
          const transferReceipt = parseFloat(status['계좌이체'] || status['온라인'] || 0);
          const insuranceClaim = parseFloat(status['청구액'] || status['보험금'] || 0);
          const healthLifeSupport = parseFloat(status['건강생활유지비'] || 0);

          dailyTotal = cashReceipt + cardReceipt + transferReceipt + insuranceClaim + healthLifeSupport;
          DEBUG && logger.info(`[Firebase] ${docDate}의 총수납 (개별항목합계-미수금제외): ${dailyTotal}`);
        }
        else if (data && data.receipts && data.receipts.total) {
          dailyTotal = data.receipts.total || 0;
          DEBUG && logger.info(`[Firebase] ${docDate}의 총수납 (receipts.total): ${dailyTotal}`);
        } else {
          DEBUG && logger.info(`[Firebase] ${docDate}에 수납 데이터 없음`);
        }

        // financialTransactions 수입 데이터 처리
        if (data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items)) {
          const financialIncomeTotal = data.financialTransactions.items
            .filter((transaction: any) => transaction.transactionType === 'income')
            .reduce((sum: number, transaction: any) => sum + (transaction.amount || 0), 0);

          if (financialIncomeTotal > 0) {
            dailyTotal += financialIncomeTotal;
            DEBUG && logger.info(`[Firebase] ${docDate}의 거래관리 수입 추가: ${financialIncomeTotal}원 (총 수납액: ${dailyTotal}원)`);
          }
        }

        monthlyReceipts.push({
          date: docDate,
          amount: dailyTotal
        });
      }
    });

    DEBUG && logger.info(`[Firebase] ${yearMonth} 월 처리된 문서: ${processedDocs}개`);
    DEBUG && logger.info(`[Firebase] 일자별 총수납 데이터:`, monthlyReceipts);

    return monthlyReceipts;
  } catch (error: any) {
    DEBUG && logger.error(' 월별 수납 데이터 로드 실패:', error);
    DEBUG && logger.error(' 오류 상세:', error.message);
    DEBUG && logger.error(' 오류 스택:', error.stack);
    throw error;
  }
};

// 특정 월의 모든 수납 아이템 데이터 가져오기 (차트번호 매칭용)
export const getMonthlyReceiptItems = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 수납 아이템 데이터 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const allReceiptItems: any[] = [];
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        DEBUG && logger.info(`[Firebase] ${yearMonth} 월 문서 발견: ${docDate}`);

        const data = doc.data();

        if (data && data.receipts && data.receipts.items && Array.isArray(data.receipts.items)) {
          const items = data.receipts.items.map((item: any) => ({
            ...item,
            receiptDate: docDate
          }));
          allReceiptItems.push(...items);
          DEBUG && logger.info(`[Firebase] ${docDate}에서 ${items.length}개 수납 아이템 발견`);
        }
      }
    });

    DEBUG && logger.info(`[Firebase] ${yearMonth} 월 처리된 문서: ${processedDocs}개`);
    DEBUG && logger.info(`[Firebase] 총 수납 아이템: ${allReceiptItems.length}개`);

    return allReceiptItems;
  } catch (error) {
    DEBUG && logger.error(' 월별 수납 아이템 데이터 로드 실패:', error);
    throw error;
  }
};

// 특정 월의 일자별 지출 데이터 가져오기
export const getMonthlyExpenseData = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 일자별 지출 데이터 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const monthlyExpenses: any[] = [];
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        DEBUG && logger.info(`[Firebase] ${yearMonth} 월 문서 발견: ${docDate}`);

        const data = doc.data();

        let dailyExpenseTotal = 0;

        if (data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items)) {
          const financialExpenseTotal = data.financialTransactions.items
            .filter((transaction: any) => transaction.transactionType === 'expense')
            .reduce((sum: number, transaction: any) => sum + (transaction.amount || 0), 0);

          if (financialExpenseTotal > 0) {
            dailyExpenseTotal = financialExpenseTotal;
            DEBUG && logger.info(`[Firebase] ${docDate}의 지출 총액 (financialTransactions): ${dailyExpenseTotal}`);
          }
        }

        if (dailyExpenseTotal === 0 && data && data.expenses) {
          if (data.expenses.total) {
            dailyExpenseTotal = data.expenses.total;
            DEBUG && logger.info(`[Firebase] ${docDate}의 지출 총액 (expenses.total): ${dailyExpenseTotal}`);
          } else if (data.expenses.items && Array.isArray(data.expenses.items)) {
            dailyExpenseTotal = data.expenses.items.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
            DEBUG && logger.info(`[Firebase] ${docDate}의 지출 총액 (expenses.items 계산): ${dailyExpenseTotal}`);
          }
        }

        if (dailyExpenseTotal === 0) {
          DEBUG && logger.info(`[Firebase] ${docDate}에 지출 데이터 없음`);
        }

        monthlyExpenses.push({
          date: docDate,
          amount: dailyExpenseTotal
        });
      }
    });

    DEBUG && logger.info(`[Firebase] ${yearMonth} 월 처리된 문서: ${processedDocs}개`);
    DEBUG && logger.info(`[Firebase] 일자별 지출 데이터:`, monthlyExpenses);

    return monthlyExpenses;
  } catch (error) {
    DEBUG && logger.error(' 월별 지출 데이터 로드 실패:', error);
    throw error;
  }
};

// 특정 월의 일자별 부가가치세 데이터 가져오기
export const getMonthlyVATData = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`🧮 [VAT 디버깅] ====== ${yearMonth} 월별 부가가치세 계산 시작 ======`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const monthlyVAT: any[] = [];
    let processedDocs = 0;
    let totalVATFound = 0;
    let docsWithVAT = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();
        let dailyVATTotal = 0;

        // 1순위: reportStatus에서 부가가치세 가져오기
        if (data && data.reportStatus && Array.isArray(data.reportStatus) && data.reportStatus.length > 0) {
          const status = data.reportStatus[0];
          dailyVATTotal = status['월총부가가치세'] || status['부가가치세'] || status['VAT'] || status['부가세'] || 0;
        }
        // 2순위: receipts.items에서 부가가치세 합계 계산
        else if (data && data.receipts && data.receipts.items && Array.isArray(data.receipts.items)) {
          dailyVATTotal = data.receipts.items.reduce((sum: number, item: any) => {
            const vat = item.valueAdded || item['부가가치세'] || item.VAT || item['부가세'] || 0;
            return sum + vat;
          }, 0);
        }
        // 3순위: patientGroups에서 부가가치세 합계 계산
        else if (data && data.patientGroups && Array.isArray(data.patientGroups)) {
          dailyVATTotal = data.patientGroups.reduce((sum: number, group: any) => {
            return sum + (group.valueAdded || 0);
          }, 0);
        }
        // 4순위: assistGroups에서 부가가치세 합계 계산
        else if (data && data.assistGroups && Array.isArray(data.assistGroups)) {
          dailyVATTotal = data.assistGroups.reduce((sum: number, group: any) => {
            return sum + (group.valueAdded || 0);
          }, 0);
        }

        if (dailyVATTotal > 0) {
          docsWithVAT++;
          totalVATFound += dailyVATTotal;
        }

        monthlyVAT.push({
          date: docDate,
          amount: dailyVATTotal
        });
      }
    });

    DEBUG && logger.info(`🧮 [VAT 디버깅] ====== ${yearMonth} 월별 부가가치세 계산 완료 ======`);
    DEBUG && logger.info(`🧮 [VAT 디버깅] 처리된 문서: ${processedDocs}개, VAT가 있는 문서: ${docsWithVAT}개, 월총부가가치세: ${totalVATFound.toLocaleString()}원`);

    return monthlyVAT;
  } catch (error: any) {
    DEBUG && logger.error('🧮 [VAT 디버깅] 월별 부가가치세 데이터 로드 실패:', error);
    throw error;
  }
};

// 월별 거래 데이터 집계 (v2: 팀 매핑 지원)
export const getMonthlyFinancialTransactionData = async (yearMonth: string, employees: any[] = [], budgetData: any = null) => {
  try {
    if (!/^\d{4}-\d{2}$/.test(yearMonth)) {
      console.error('[Firebase] Invalid yearMonth format:', yearMonth);
      return {
        yearMonth,
        totalExpense: 0,
        totalIncome: 0,
        netAmount: 0,
        expenseByCategory: {},
        incomeByCategory: {},
        transactionsByTeam: {},
        dailyBreakdown: [],
        itemCount: 0
      };
    }

    DEBUG && logger.info(`[Firebase] 월별 거래 데이터 로드 시작: ${yearMonth}`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const result: any = {
      yearMonth,
      totalExpense: 0,
      totalIncome: 0,
      netAmount: 0,
      expenseByCategory: {},
      incomeByCategory: {},
      transactionsByTeam: {},
      dailyBreakdown: [],
      itemCount: 0
    };

    // NOTE: dynamic import path updated for new directory structure
    const { selectTargetEmployees, buildEmployeeTeamMap, getTeamForAssist } = await import('../teamMonthlyDataService.ts');

    const targetEmployees = selectTargetEmployees(employees, budgetData, yearMonth);
    const employeeTeamMap = buildEmployeeTeamMap(targetEmployees);

    DEBUG && logger.info(`[Firebase] 월별 거래 데이터: ${yearMonth}, 직원 ${targetEmployees.length}명, 팀 매핑 ${Object.keys(employeeTeamMap).length}개`);

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        const data = doc.data();
        let financialData = data.financialTransactions;

        if (!financialData && data.expenses) {
          financialData = {
            totalExpense: data.expenses.total || 0,
            totalIncome: 0,
            items: (data.expenses.items || []).map((item: any) => ({
              ...item,
              transactionType: 'expense',
              category: item.category || '지출'
            }))
          };
        }

        if (financialData) {
          result.dailyBreakdown.push({
            date: docDate,
            expense: financialData.totalExpense || 0,
            income: financialData.totalIncome || 0
          });

          result.totalExpense += financialData.totalExpense || 0;
          result.totalIncome += financialData.totalIncome || 0;

          (financialData.items || []).forEach((item: any) => {
            result.itemCount++;

            const category = item.category || '기타';

            const team = getTeamForAssist(
              item.assistName,
              employeeTeamMap,
              item.cashierName,
              item.assistTeam,
              null,
              null
            );

            if (item.transactionType === 'expense') {
              result.expenseByCategory[category] =
                (result.expenseByCategory[category] || 0) + (item.amount || 0);
            } else {
              result.incomeByCategory[category] =
                (result.incomeByCategory[category] || 0) + (item.amount || 0);
            }

            if (!result.transactionsByTeam[team]) {
              result.transactionsByTeam[team] = { expense: 0, income: 0 };
            }
            if (item.transactionType === 'expense') {
              result.transactionsByTeam[team].expense += (item.amount || 0);
            } else {
              result.transactionsByTeam[team].income += (item.amount || 0);
            }
          });
        }
      }
    });

    result.netAmount = result.totalIncome - result.totalExpense;
    result.dailyBreakdown.sort((a: any, b: any) => a.date.localeCompare(b.date));

    DEBUG && logger.info(`[Firebase] 월별 거래 데이터 집계 완료: ${yearMonth}`, {
      totalExpense: result.totalExpense,
      totalIncome: result.totalIncome,
      netAmount: result.netAmount,
      itemCount: result.itemCount,
      teamCount: Object.keys(result.transactionsByTeam).length
    });

    return result;
  } catch (error) {
    console.error('[Firebase] 월별 거래 데이터 로드 실패:', error);
    throw error;
  }
};

// 특정 월의 팀별 지출 데이터 가져오기 (최적화된 버전)
export const getMonthlyExpenseDataByTeam = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`🔍 [Firebase 팀별 지출] ${yearMonth} 월별 팀별 지출 데이터 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const employees = await getCachedEmployees();
    const employeeTeamMap: Record<string, string> = {};
    employees.forEach((emp: any) => {
      employeeTeamMap[emp.name] = emp.team || '미지정';
    });

    DEBUG && logger.info(`🔍 [Firebase 팀별 지출] 직원 매핑 정보:`, {
      employeesCount: employees.length,
      teamMappingCount: Object.keys(employeeTeamMap).length,
      teams: [...new Set(Object.values(employeeTeamMap))].sort()
    });

    const teamExpenseData: Record<string, any[]> = {};
    const assistExpenseTracker = new Map();
    let processedDocs = 0;
    let totalItems = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();

        const dailyTeamAssistMap: Record<string, string> = {};

        if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
          data.teamCollection.forEach((teamData: any) => {
            const assistName = teamData.assistName || '';
            if (teamData.isAssistRow && assistName && assistName !== '미지정') {
              const teamName = teamData.team || '미지정';
              dailyTeamAssistMap[assistName] = teamName;
            }
          });
        }

        const allExpenseItems: any[] = [];

        if (data && data.financialTransactions && data.financialTransactions.items) {
          const financialExpenses = data.financialTransactions.items.filter((item: any) => item.transactionType === 'expense');
          allExpenseItems.push(...financialExpenses);
        }

        if (allExpenseItems.length === 0 && data && data.expenses && data.expenses.items) {
          allExpenseItems.push(...data.expenses.items);
        }

        if (allExpenseItems.length > 0) {
          allExpenseItems.forEach((expense: any) => {
            const assistName = expense.assistName || '미지정';
            const amount = expense.amount || 0;

            let teamName = dailyTeamAssistMap[assistName] || employeeTeamMap[assistName] || '미지정';

            if (!teamExpenseData[teamName]) {
              teamExpenseData[teamName] = [];
            }

            teamExpenseData[teamName].push({
              date: docDate,
              amount: amount,
              assistName: assistName
            });

            const trackingKey = `${teamName}_${assistName}`;
            if (!assistExpenseTracker.has(trackingKey)) {
              assistExpenseTracker.set(trackingKey, {
                team: teamName,
                assistName: assistName,
                totalAmount: 0,
                dates: [],
                itemCount: 0
              });
            }

            const tracker = assistExpenseTracker.get(trackingKey);
            tracker.totalAmount += amount;
            tracker.dates.push(docDate);
            tracker.itemCount++;

            totalItems++;
          });
        }
      }
    });

    DEBUG && logger.info(`✅ [Firebase 팀별 지출] ${yearMonth} 수집 완료:`, {
      processedDocs,
      totalItems,
      teams: Object.keys(teamExpenseData).sort()
    });

    return teamExpenseData;
  } catch (error) {
    DEBUG && logger.error('🔴 [Firebase 팀별 지출] 로드 실패:', error);
    throw error;
  }
};

// 특정 월의 팀별 수납 데이터 가져오기 (최적화된 버전)
export const getMonthlyReceiptDataByTeam = async (yearMonth: string, externalEmployees: any[] | null = null) => {
  try {
    DEBUG && logger.info(`🔍 [Firebase 월 수납액 검증] ${yearMonth} 월별 팀별 수납 데이터 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const cachedEmployees = await getCachedEmployees();
    const employeeTeamMap: Record<string, string> = {};

    cachedEmployees.forEach((emp: any) => {
      employeeTeamMap[emp.name] = emp.team || '미지정';
    });

    if (externalEmployees && externalEmployees.length > 0) {
      externalEmployees.forEach((emp: any) => {
        employeeTeamMap[emp.name] = emp.team || '미지정';
      });
    }

    DEBUG && logger.info(`🔍 [Firebase 월 수납액 검증] 직원 매핑 정보:`, {
      cachedEmployeesCount: cachedEmployees.length,
      externalEmployeesCount: externalEmployees?.length || 0,
      teamMappingCount: Object.keys(employeeTeamMap).length,
      teams: [...new Set(Object.values(employeeTeamMap))].sort()
    });

    const teamReceiptData: Record<string, any[]> = {};
    const assistReceiptTracker = new Map();
    let processedDocs = 0;
    let totalItems = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();

        const dailyTeamAssistMap: Record<string, string> = {};
        if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
          data.teamCollection.forEach((teamData: any) => {
            if (teamData && teamData.isAssistRow) {
              const assistName = teamData.assistName || teamData.assist || teamData.name;
              if (assistName && assistName !== '미지정') {
                const teamName = teamData.team || '미지정';
                dailyTeamAssistMap[assistName] = teamName;
              }
            }
          });
        }

        // assistReceipts에서 어시스트별 수납 데이터 가져오기
        if (data && data.assistReceipts && Array.isArray(data.assistReceipts)) {
          data.assistReceipts.forEach((receipt: any) => {
            const assistName = receipt.assistName || '미지정';
            const cashierName = receipt.cashierName || '';
            const amount = receipt.amount || 0;

            let teamName = dailyTeamAssistMap[assistName] || employeeTeamMap[assistName];
            let usedCashierFallback = false;

            if (!teamName && cashierName) {
              teamName = dailyTeamAssistMap[cashierName] || employeeTeamMap[cashierName];
              usedCashierFallback = !!teamName;
            }
            teamName = teamName || '미지정';

            if (usedCashierFallback) {
              console.log(`💰 [수납자 폴백] ${assistName} → ${teamName} (수납자: ${cashierName})`);
            }

            if (!teamReceiptData[teamName]) {
              teamReceiptData[teamName] = [];
            }

            teamReceiptData[teamName].push({
              date: docDate,
              amount: amount,
              assistName: assistName
            });

            if (!assistReceiptTracker.has(assistName)) {
              assistReceiptTracker.set(assistName, {
                team: teamName,
                totalAmount: 0,
                dates: [],
                itemCount: 0
              });
            }

            const tracker = assistReceiptTracker.get(assistName);
            tracker.totalAmount += amount;
            tracker.dates.push(docDate);
            tracker.itemCount++;

            totalItems++;
          });
        }

        // financialTransactions 수입 데이터 처리
        if (data && data.financialTransactions && data.financialTransactions.items && Array.isArray(data.financialTransactions.items)) {
          data.financialTransactions.items
            .filter((transaction: any) => transaction.transactionType === 'income')
            .forEach((transaction: any) => {
              const assistName = transaction.assistName || '미지정';
              const amount = transaction.amount || 0;

              // 그 날 teamCollection의 팀 매핑(dailyTeamAssistMap)을 우선 확인한 뒤 전역 맵으로 폴백.
              // (assistReceipts·expense 경로와 동일 — 캐시/외부 목록에 없고 그 날 teamCollection에만
              //  있는 어시스트의 수입이 '미지정'으로 오귀속되던 문제 방지)
              const teamName = dailyTeamAssistMap[assistName] || employeeTeamMap[assistName] || '미지정';

              if (!teamReceiptData[teamName]) {
                teamReceiptData[teamName] = [];
              }

              teamReceiptData[teamName].push({
                date: docDate,
                amount: amount,
                assistName: assistName,
                source: 'financialTransaction',
                transactionType: 'income',
                description: transaction.description || '거래관리 수입'
              });

              if (!assistReceiptTracker.has(assistName)) {
                assistReceiptTracker.set(assistName, {
                  team: teamName,
                  totalAmount: 0,
                  dates: [],
                  itemCount: 0
                });
              }

              const tracker = assistReceiptTracker.get(assistName);
              tracker.totalAmount += amount;
              tracker.dates.push(docDate);
              tracker.itemCount++;

              totalItems++;
            });
        }
      }
    });

    DEBUG && logger.info(`✅ [Firebase 월 수납액 검증] ${yearMonth} 수집 완료:`, {
      processedDocs,
      totalItems,
      teams: Object.keys(teamReceiptData).sort()
    });

    return teamReceiptData;
  } catch (error) {
    DEBUG && logger.error('🔴 [Firebase 월 수납액 검증] 로드 실패:', error);
    throw error;
  }
};

// 특정 월의 모든 진료의사 목록 가져오기
export const getMonthlyDoctorsList = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 진료의사 목록 요청 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const doctorsList = new Set<string>();
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();

        if (data && data.assistReceipts && Array.isArray(data.assistReceipts)) {
          data.assistReceipts.forEach((receipt: any) => {
            if (receipt.doctorName && receipt.doctorName.trim() !== '') {
              doctorsList.add(receipt.doctorName.trim());
            }
          });
        }
      }
    });

    const doctorsArray = Array.from(doctorsList).sort();
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월 진료의사 목록:`, doctorsArray);

    return doctorsArray;
  } catch (error) {
    DEBUG && logger.error(' 월별 진료의사 목록 로드 실패:', error);
    throw error;
  }
};

// 특정 어시스트의 월별 수납 데이터 가져오기 (팀별 분리)
export const getMonthlyReceiptDataByAssist = async (yearMonth: string, assistName: string, teamName: string) => {
  try {
    DEBUG && logger.info(`🔍 [Firebase 어시스트별 수납액] ${yearMonth}/${teamName}/${assistName} 조회 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const assistReceiptData: any[] = [];
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();

        if (data && data.assistReceipts && Array.isArray(data.assistReceipts)) {
          data.assistReceipts.forEach((receipt: any) => {
            if (receipt.assistName === assistName) {
              assistReceiptData.push({
                date: docDate,
                amount: receipt.amount || 0,
                assistName: receipt.assistName,
                teamName: teamName,
                treatmentType: receipt.treatmentType
              });
            }
          });
        }

        if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
          data.teamCollection.forEach((teamItem: any) => {
            if (teamItem.assistName === assistName &&
                teamItem.team === teamName &&
                teamItem.isAssistRow) {
              const alreadyProcessed = assistReceiptData.some((item: any) =>
                item.date === docDate && item.assistName === assistName
              );

              if (!alreadyProcessed) {
                assistReceiptData.push({
                  date: docDate,
                  amount: teamItem.amount || 0,
                  assistName: teamItem.assistName,
                  teamName: teamItem.team,
                  treatmentType: teamItem.treatmentType || '기타'
                });
              }
            }
          });
        }
      }
    });

    const totalAmount = assistReceiptData.reduce((sum: number, item: any) => sum + item.amount, 0);

    DEBUG && logger.info(`✅ [Firebase 어시스트별 수납액] ${assistName} (${teamName}) ${yearMonth}:`, {
      totalAmount: totalAmount.toLocaleString(),
      recordCount: assistReceiptData.length,
      processedDocs: processedDocs
    });

    return assistReceiptData;
  } catch (error) {
    DEBUG && logger.error(`🔴 [Firebase 어시스트별 수납액] ${assistName} 조회 실패:`, { error });
    return [];
  }
};

// 특정 어시스트의 월별 지출 데이터 가져오기 (팀별 분리)
export const getMonthlyExpenseDataByAssist = async (yearMonth: string, assistName: string, teamName: string) => {
  try {
    DEBUG && logger.info(`🔍 [Firebase 어시스트별 지출액] ${yearMonth}/${teamName}/${assistName} 조회 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const assistExpenseData: any[] = [];
    let processedDocs = 0;

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        processedDocs++;
        const data = doc.data();

        const expenseItems: any[] = [];

        if (data && data.financialTransactions && data.financialTransactions.items) {
          const financialExpenses = data.financialTransactions.items.filter((item: any) => item.transactionType === 'expense');
          expenseItems.push(...financialExpenses);
        }

        if (data && data.expenses && data.expenses.items) {
          expenseItems.push(...data.expenses.items);
        }

        expenseItems.forEach((expense: any) => {
          if (expense.assistName === assistName) {
            assistExpenseData.push({
              date: docDate,
              amount: expense.amount || 0,
              assistName: expense.assistName,
              teamName: teamName,
              description: expense.description
            });
          }
        });

        if (data && data.teamCollection && Array.isArray(data.teamCollection)) {
          data.teamCollection.forEach((teamItem: any) => {
            if (teamItem.assistName === assistName &&
                teamItem.team === teamName &&
                teamItem.isAssistRow &&
                teamItem.monthlyExpense > 0) {
              const alreadyProcessed = assistExpenseData.some((item: any) =>
                item.date === docDate && item.assistName === assistName
              );

              if (!alreadyProcessed) {
                assistExpenseData.push({
                  date: docDate,
                  amount: teamItem.monthlyExpense || 0,
                  assistName: teamItem.assistName,
                  teamName: teamItem.team,
                  description: '팀컬렉션 데이터'
                });
              }
            }
          });
        }
      }
    });

    const totalAmount = assistExpenseData.reduce((sum: number, item: any) => sum + item.amount, 0);

    DEBUG && logger.info(`✅ [Firebase 어시스트별 지출액] ${assistName} (${teamName}) ${yearMonth}:`, {
      totalAmount: totalAmount.toLocaleString(),
      recordCount: assistExpenseData.length,
      processedDocs: processedDocs
    });

    return assistExpenseData;
  } catch (error) {
    DEBUG && logger.error(`🔴 [Firebase 어시스트별 지출액] ${assistName} 조회 실패:`, { error });
    return [];
  }
};

// 월별 teamCollection 데이터 업데이트 (compositeKey 기준 집계 + 검증 + 누락 감지)
// options.acceptDeflation: 디플레이션 게이트 발화 시에도 저장 진행(호출부에서 사용자 승인 후 재시도용)
export const updateMonthlyTeamCollection = async (
  yearMonth: string,
  options?: { acceptDeflation?: boolean }
) => {
  try {
    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 teamCollection 업데이트 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const monthlyCompositeData: Record<string, any> = {};
    const dailyDocuments: Record<string, any> = {};
    const dataInconsistencies: any[] = [];
    const skippedItems: any[] = [];

    snapshot.docs.forEach(docSnap => {
      const docDate = docSnap.id;

      if (docDate.startsWith(yearMonth)) {
        const data = docSnap.data();
        dailyDocuments[docDate] = data;

        if (data.teamCollection && Array.isArray(data.teamCollection)) {
          data.teamCollection.forEach((item: any, index: number) => {
            const reasons: string[] = [];
            if (!item.isAssistRow) reasons.push('isAssistRow=false');
            if (!item.compositeKey) reasons.push('compositeKey 없음');
            if (!item.assistName) reasons.push('assistName 없음');
            if (!item.team) reasons.push('team 없음');

            if (reasons.length > 0) {
              skippedItems.push({
                date: docDate,
                index: index,
                reasons: reasons,
                item: {
                  assistName: item.assistName,
                  team: item.team,
                  compositeKey: item.compositeKey,
                  isAssistRow: item.isAssistRow,
                  amount: item.amount,
                  originalAssistName: item.originalAssistName
                }
              });
            }

            if (item.isAssistRow && item.compositeKey && item.assistName && item.team) {
              const compositeKey = item.compositeKey;
              const assistName = item.assistName;
              const team = item.team;

              const expectedCompositeKey = `${assistName}-${team}`;
              if (compositeKey !== expectedCompositeKey) {
                const originalAssist = item.originalAssistName || assistName;
                const correctCompositeKey = `${originalAssist}-${team}`;

                dataInconsistencies.push({
                  date: docDate,
                  index: index,
                  assistName: assistName,
                  team: team,
                  currentCompositeKey: compositeKey,
                  expectedCompositeKey: expectedCompositeKey,
                  correctCompositeKey: correctCompositeKey,
                  originalAssistName: item.originalAssistName,
                  isCashierMapped: item.isCashierMapped
                });
              }

              const keyToUse = item.originalAssistName ? `${item.originalAssistName}-${team}` : compositeKey;

              if (!monthlyCompositeData[keyToUse]) {
                monthlyCompositeData[keyToUse] = {
                  assistName: item.originalAssistName || assistName,
                  realAssistName: assistName,
                  team: team,
                  totalReceipt: 0,
                  totalExpense: 0,
                  dailyAmounts: []
                };
              }

              const dailyReceipt = item.amount || 0;

              let dailyExpense = 0;
              if (data.expenses && data.expenses.items && Array.isArray(data.expenses.items)) {
                dailyExpense = data.expenses.items
                  .filter((expense: any) => expense.assistName === (item.originalAssistName || assistName))
                  .reduce((sum: number, expense: any) => sum + (expense.amount || 0), 0);
              }

              monthlyCompositeData[keyToUse].totalReceipt += dailyReceipt;
              monthlyCompositeData[keyToUse].totalExpense += dailyExpense;
              monthlyCompositeData[keyToUse].dailyAmounts.push({
                date: docDate,
                receipt: dailyReceipt,
                expense: dailyExpense
              });
            }
          });
        }
      }
    });

    DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 집계 완료:`, {
      compositeKeyCount: Object.keys(monthlyCompositeData).length,
      documentCount: Object.keys(dailyDocuments).length,
      dataInconsistencyCount: dataInconsistencies.length,
      skippedItemsCount: skippedItems.length
    });

    // 🛡️ 디플레이션 게이트: 재계산된 월누계 총합이 기존 저장 총합 대비 크게 감소하면 경고
    //    (의사/소개 귀속 행 소실 등 원천 손상 신호 — 2026-06 27,361,490원 소실 사고 감지용)
    //    발화 시 저장을 커밋하기 전에 경고 정보를 반환해 호출부의 사용자 승인 후에만 재시도하도록 함
    //    (경고만 남기고 저장하면 기준선이 파괴되어 소실 증거가 사라짐)
    let deflationWarning: { oldTotal: number; newTotal: number; ratio: number; collapsedDays: CollapsedDay[] } | undefined;
    {
      const existingMaxByKey: Record<string, number> = {};
      Object.values(dailyDocuments).forEach((docData: any) => {
        (docData.teamCollection || []).forEach((item: any) => {
          if (item?.isAssistRow && item.compositeKey) {
            const k = item.originalAssistName ? `${item.originalAssistName}-${item.team}` : item.compositeKey;
            existingMaxByKey[k] = Math.max(existingMaxByKey[k] || 0, item.monthlyReceipt || 0);
          }
        });
      });
      const oldTotal = Object.values(existingMaxByKey).reduce((s, v) => s + v, 0);
      const newTotal = Object.values(monthlyCompositeData).reduce((s: number, m: any) => s + (m.totalReceipt || 0), 0);
      const decrease = oldTotal - newTotal;
      // 상대(10% 초과 감소) 또는 절대(100만원 이상 감소) 임계 — 큰 월은 비율이 작아도 절대액으로 감지
      if (oldTotal > 0 && (newTotal < oldTotal * 0.9 || decrease >= 1_000_000)) {
        // 🔍 왜 줄어드는가를 함께 알려준다 — '명세가 지워진 날'과 '정당한 감소'는 다른 사안이다.
        //    호출부는 collapsedDays 가 비어 있지 않으면 저장을 차단한다(정책 결정 2026-09-06).
        //    문서는 이미 전부 읽어 두었으므로 추가 조회가 없다.
        const collapsedDays = findCollapsedDays(
          Object.entries(dailyDocuments).map(([date, docData]: [string, any]) => ({
            date,
            items: docData?.receipts?.items,
          }))
        );
        deflationWarning = { oldTotal, newTotal, ratio: newTotal / oldTotal, collapsedDays };
        console.warn(
          `🛡️ [updateMonthlyTeamCollection] ${yearMonth} 월누계 총합 급감 감지: ` +
          `${oldTotal.toLocaleString()}원 → ${newTotal.toLocaleString()}원 (−${decrease.toLocaleString()}원). ` +
          `의사/소개 귀속 행 소실 등 teamCollection 원천 손상 가능성 — 재작성 내용을 확인하세요.`
        );
        if (!options?.acceptDeflation) {
          return {
            success: false as const,
            updatedDocuments: 0,
            monthlyData: monthlyCompositeData,
            dataInconsistencies: dataInconsistencies.length,
            deflationWarning
          };
        }
      }
    }

    // 각 날짜별 문서의 teamCollection 업데이트
    const updatePromises: Promise<void>[] = [];

    Object.entries(dailyDocuments).forEach(([docDate, docData]) => {
      if (docData.teamCollection && Array.isArray(docData.teamCollection)) {
        let hasUpdates = false;

        const updatedTeamCollection = docData.teamCollection.map((item: any) => {
          if (item.isAssistRow && item.compositeKey) {
            const correctCompositeKey = item.originalAssistName ?
              `${item.originalAssistName}-${item.team}` :
              item.compositeKey;

            if (monthlyCompositeData[correctCompositeKey]) {
              const monthlyData = monthlyCompositeData[correctCompositeKey];
              const monthlyReceipt = monthlyData.totalReceipt;
              const monthlyExpense = monthlyData.totalExpense;
              const monthlyProfit = monthlyReceipt - monthlyExpense;

              const needsUpdate =
                item.monthlyReceipt !== monthlyReceipt ||
                item.monthlyExpense !== monthlyExpense ||
                item.monthlyProfit !== monthlyProfit ||
                item.compositeKey !== correctCompositeKey;

              if (needsUpdate) {
                hasUpdates = true;
                return {
                  ...item,
                  compositeKey: correctCompositeKey,
                  monthlyReceipt: monthlyReceipt,
                  monthlyExpense: monthlyExpense,
                  monthlyProfit: monthlyProfit,
                  receiptTotal: monthlyReceipt,
                  expenseTotal: monthlyExpense
                };
              }
            }
          }
          return item;
        });

        const finalTeamCollection = updatedTeamCollection;

        if (hasUpdates) {
          // stale 스냅샷(docData) 전체를 다시 쓰면, read~write 사이에 다른
          // 프로세스가 갱신한 receipts·financialTransactions 등이 옛 값으로
          // 되돌아간다. merge:true 이므로 변경 필드만 기록한다.
          const updatePromise = setDoc(doc(db, 'dailyReports', docDate), {
            teamCollection: finalTeamCollection,
            updatedAt: Timestamp.now()
          }, { merge: true });
          updatePromises.push(updatePromise);
        }
      }
    });

    if (updatePromises.length > 0) {
      await Promise.all(updatePromises);
      DEBUG && logger.info(`[Firebase] ${yearMonth} 월별 teamCollection 업데이트 완료: ${updatePromises.length}개 문서 업데이트됨`);
    }

    return {
      success: true as const,
      updatedDocuments: updatePromises.length,
      monthlyData: monthlyCompositeData,
      dataInconsistencies: dataInconsistencies.length,
      deflationWarning // acceptDeflation으로 진행한 경우에도 감지 정보 전달
    };

  } catch (error) {
    DEBUG && logger.error(`[Firebase] ${yearMonth} 월별 teamCollection 업데이트 실패:`, { error });
    throw error;
  }
};

// 월별 수납액 누락 분석 함수
export const analyzeMonthlyReceiptGaps = async (yearMonth: string) => {
  try {
    DEBUG && logger.info(`[🔍 누락 분석] ${yearMonth} 월별 수납액 누락 분석 시작`);

    const monthlyQuery = getMonthlyReportsQuery(yearMonth);
    const snapshot = await getDocs(monthlyQuery);

    const analysisResult: any = {
      totalDocuments: 0,
      processedDocuments: 0,
      teamCollectionItems: 0,
      assistRowItems: 0,
      validAssistRowItems: 0,
      skippedByReason: {
        'isAssistRow=false': 0,
        'compositeKey 없음': 0,
        'assistName 없음': 0,
        'team 없음': 0,
        'amount=0': 0
      },
      sampleSkippedItems: [],
      receiptTotalBySource: {
        teamCollection: 0,
        directReceipts: 0,
        assistReceipts: 0
      }
    };

    snapshot.docs.forEach(doc => {
      const docDate = doc.id;

      if (docDate.startsWith(yearMonth)) {
        analysisResult.totalDocuments++;
        const data = doc.data();

        if (data) {
          analysisResult.processedDocuments++;

          if (data.teamCollection && Array.isArray(data.teamCollection)) {
            data.teamCollection.forEach((item: any, index: number) => {
              analysisResult.teamCollectionItems++;

              if (item.isAssistRow) {
                analysisResult.assistRowItems++;

                const reasons: string[] = [];
                if (!item.compositeKey) reasons.push('compositeKey 없음');
                if (!item.assistName) reasons.push('assistName 없음');
                if (!item.team) reasons.push('team 없음');
                if ((item.amount || 0) === 0) reasons.push('amount=0');

                if (reasons.length > 0) {
                  reasons.forEach(reason => {
                    if (analysisResult.skippedByReason[reason] !== undefined) {
                      analysisResult.skippedByReason[reason]++;
                    }
                  });

                  if (analysisResult.sampleSkippedItems.length < 5) {
                    analysisResult.sampleSkippedItems.push({
                      date: docDate,
                      index: index,
                      reasons: reasons,
                      item: {
                        assistName: item.assistName,
                        team: item.team,
                        compositeKey: item.compositeKey,
                        amount: item.amount,
                        originalAssistName: item.originalAssistName,
                        isCashierMapped: item.isCashierMapped
                      }
                    });
                  }
                } else {
                  analysisResult.validAssistRowItems++;
                  analysisResult.receiptTotalBySource.teamCollection += (item.amount || 0);
                }
              } else {
                analysisResult.skippedByReason['isAssistRow=false']++;
              }
            });
          }

          if (data.receipts && data.receipts.items) {
            const receiptTotal = data.receipts.items.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
            analysisResult.receiptTotalBySource.directReceipts += receiptTotal;
          }

          if (data.assistReceipts && Array.isArray(data.assistReceipts)) {
            const assistTotal = data.assistReceipts.reduce((sum: number, item: any) => sum + (item.amount || 0), 0);
            analysisResult.receiptTotalBySource.assistReceipts += assistTotal;
          }
        }
      }
    });

    DEBUG && logger.info(`[🔍 누락 분석] ${yearMonth} 분석 완료:`, analysisResult);

    return analysisResult;

  } catch (error) {
    DEBUG && logger.error(`[🔍 누락 분석] ${yearMonth} 분석 실패:`, { error });
    throw error;
  }
};


/**
 * 그 달에 '명세가 무너진 날'이 있는지 — 화면 상단 안내용.
 *
 * 저장 경로의 차단 판정과 **같은 순수 함수(findCollapsedDays)** 를 쓴다. 두 곳이 갈라지면
 * "화면은 멀쩡하다는데 저장이 막힌다"는 설명 불가능한 상태가 된다.
 */
export const getMonthlyCollapsedDays = async (yearMonth: string): Promise<CollapsedDay[]> => {
  const snapshot = await getDocs(getMonthlyReportsQuery(yearMonth));
  return findCollapsedDays(
    snapshot.docs.map((d) => ({ date: d.id, items: (d.data() as any)?.receipts?.items }))
  );
};
